<notifications-nav></notifications-nav>

<div class="notifications-container">
  <ng-container *ngIf="notifications$ | async as notifications">
    <div *ngIf="(isLoading$ | async) || (isActionRunning$ | async)" class="spinner-container">
      <div class="spinner"></div>
    </div>

    <div *ngIf="errorMessage$ | async as errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="header" *ngIf="!(isLoading$ | async)">
      <div>
        <h2>Notifications</h2>
        <p class="subtitle">Review and approve required notifications.</p>
      </div>

      <div class="header-actions">
        <div class="week-pill" *ngIf="currentFullYearWeek$ | async as week">
          Week {{ week }}
        </div>

        <button
          class="btn btn-primary"
          type="button"
          (click)="approveAll()"
          [disabled]="!getPendingActionCount(notifications) || (isLoading$ | async) || (isActionRunning$ | async)">
          Approve All
        </button>
      </div>
    </div>

    <div class="empty-state" *ngIf="!(isLoading$ | async) && !hasAnyNotifications(notifications)">
      <div class="empty-icon">✓</div>
      <h3>No notifications</h3>
      <p>You do not have any pending approvals right now.</p>
    </div>

    <!-- Time Off -->
    <div class="notifications-list" *ngIf="activeTab === 'timeOff' && getTimeOffCount(notifications)">
      <div
        class="notification-row timeoff-row"
        *ngFor="let group of notifications.dayOffRequestGroupsNeedToApproveBySupervisor; trackBy: trackById">
        <div class="notification-info">
          <div class="notification-name">
            {{ getEmployeeName(group.employee) }}
            <span class="employee-number">{{ getEmployeeNumber(group.employee) }}</span>
          </div>

          <div class="notification-description">
            {{ group.payCode.name }} • {{ getDayOffTotalHours(group) | number: '1.2-2' }}h
          </div>

          <div class="notification-meta">
            {{ getDayOffDatesLabel(group) }}
          </div>

          <div class="reason-box" *ngIf="group.reason">
            {{ group.reason }}
          </div>
        </div>

        <div class="notification-status">
          <span class="status-badge pending">Time Off</span>
        </div>

        <div class="notification-actions" *ngIf="isDayOffActionable(group); else resolvedDayOff">
          <button class="btn btn-danger" type="button" (click)="rejectDayOff(group)" [disabled]="isActionRunning$ | async">
            Reject
          </button>

          <button class="btn btn-primary" type="button" (click)="approveDayOff(group)" [disabled]="isActionRunning$ | async">
            Approve
          </button>
        </div>

        <ng-template #resolvedDayOff>
          <div class="notification-actions">
            <span class="status-badge approved" *ngIf="group.isApproved === true">Approved</span>
            <span class="status-badge rejected" *ngIf="group.isApproved === false">Rejected</span>
          </div>
        </ng-template>
      </div>
    </div>

    <div class="empty-state compact" *ngIf="activeTab === 'timeOff' && !getTimeOffCount(notifications)">
      No time off notifications.
    </div>

    <!-- Supervisor Hours -->
    <div class="notifications-list" *ngIf="activeTab === 'supervisorHours' && getSupervisorHoursCount(notifications)">
      <div
        class="notification-row hours-row"
        *ngFor="let row of notifications.bookedHoursNeedToApproveBySupervisor; trackBy: trackById">
        <div class="notification-info">
          <div class="notification-name">
            {{ getEmployeeName(row.employee) }}
            <span class="employee-number">{{ getEmployeeNumber(row.employee) }}</span>
          </div>

          <div class="notification-description">
            {{ row.bookedDate | date: 'shortDate' }} • {{ row.hours | number: '1.2-2' }}h • {{ row.payCode?.name || '—' }}
          </div>

          <div class="notification-meta compact-details">
            <span>{{ getBookedHourCompany(row) }}</span>
            <span>{{ getBookedHourDepartment(row) }}</span>
            <span>{{ getBookedHourWorkerType(row) }}</span>
          </div>

          <div class="notification-meta">
            {{ row.workOrderNumber?.won || '—' }}
            <span *ngIf="row.workOrderNumber?.description"> — {{ row.workOrderNumber?.description }}</span>
          </div>
        </div>

        <div class="notification-actions">
          <button
            class="btn btn-danger"
            type="button"
            (click)="rejectSupervisorBookedHour(row)"
            [disabled]="isActionRunning$ | async">
            Reject
          </button>

          <button
            class="btn btn-secondary"
            type="button"
            (click)="openSupervisorBookedHourChange(row)"
            [disabled]="isActionRunning$ | async">
            Change
          </button>

          <button
            class="btn btn-primary"
            type="button"
            (click)="approveSupervisorBookedHour(row)"
            [disabled]="isActionRunning$ | async">
            Approve
          </button>
        </div>
      </div>
    </div>

    <div class="empty-state compact" *ngIf="activeTab === 'supervisorHours' && !getSupervisorHoursCount(notifications)">
      No supervisor hour notifications.
    </div>

    <!-- My Hours -->
    <div class="notifications-list" *ngIf="activeTab === 'myHours' && getMyHoursCount(notifications)">
      <div
        class="notification-row hours-row"
        *ngFor="let row of notifications.bookedHoursNeedToApproveBySelf; trackBy: trackById">
        <div class="notification-info">
          <div class="notification-name">
            {{ getEmployeeName(row.employee) }}
            <span class="employee-number">{{ getEmployeeNumber(row.employee) }}</span>
          </div>

          <div class="notification-description">
            {{ row.bookedDate | date: 'shortDate' }} • {{ row.hours | number: '1.2-2' }}h • {{ row.payCode?.name || '—' }}
          </div>

          <div class="notification-meta compact-details">
            <span>{{ getBookedHourCompany(row) }}</span>
            <span>{{ getBookedHourDepartment(row) }}</span>
            <span>{{ getBookedHourWorkerType(row) }}</span>
          </div>

          <div class="notification-meta">
            {{ row.workOrderNumber?.won || '—' }}
            <span *ngIf="row.workOrderNumber?.description"> — {{ row.workOrderNumber?.description }}</span>
          </div>
        </div>

        <div class="notification-actions">
          <button
            class="btn btn-danger"
            type="button"
            (click)="rejectSelfBookedHour(row)"
            [disabled]="isActionRunning$ | async">
            Reject
          </button>

          <button
            class="btn btn-secondary"
            type="button"
            (click)="openSelfBookedHourChange(row)"
            [disabled]="isActionRunning$ | async">
            Request Change
          </button>

          <button
            class="btn btn-primary"
            type="button"
            (click)="approveSelfBookedHour(row)"
            [disabled]="isActionRunning$ | async">
            Approve
          </button>
        </div>
      </div>
    </div>

    <div class="empty-state compact" *ngIf="activeTab === 'myHours' && !getMyHoursCount(notifications)">
      No personal hour notifications.
    </div>
  </ng-container>
</div>

<div class="modal-backdrop" *ngIf="bookedHourChangeModal as modal">
  <div class="modal change-hours-modal" (click)="$event.stopPropagation()">
    <h2>Change Booked Hours</h2>

    <div class="change-summary">
      <div>
        <span>Employee</span>
        <strong>{{ getEmployeeName(modal.row.employee) }}</strong>
      </div>

      <div>
        <span>Date</span>
        <strong>{{ modal.row.bookedDate | date: 'shortDate' }}</strong>
      </div>

      <div>
        <span>Current Hours</span>
        <strong>{{ modal.row.hours | number: '1.2-2' }}</strong>
      </div>

      <div>
        <span>Pay Code</span>
        <strong>{{ modal.row.payCode?.name || '—' }}</strong>
      </div>

      <div>
        <span>Company</span>
        <strong>{{ getBookedHourCompany(modal.row) }}</strong>
      </div>

      <div>
        <span>Department</span>
        <strong>{{ getBookedHourDepartment(modal.row) }}</strong>
      </div>

      <div>
        <span>Worker Type</span>
        <strong>{{ getBookedHourWorkerType(modal.row) }}</strong>
      </div>

      <div>
        <span>Week</span>
        <strong>{{ getBookedHourYyww(modal.row) }}</strong>
      </div>

      <div class="wide">
        <span>WON</span>
        <strong>{{ modal.row.workOrderNumber?.won || '—' }}</strong>
      </div>
    </div>

    <label for="newBookedHours">New booked hours</label>
    <input
      id="newBookedHours"
      type="number"
      min="0"
      step="0.25"
      inputmode="decimal"
      [ngModel]="modal.newBookedHours"
      (ngModelChange)="onChangeHoursInput($event)" />

    <label class="checkbox-label" *ngIf="!modal.isSelfApproval">
      <input
        type="checkbox"
        [ngModel]="modal.bypassEmployeeApproval"
        (ngModelChange)="onBypassEmployeeApprovalChange($event)" />
      Bypass employee approval and approve immediately
    </label>

    <div class="change-note" *ngIf="modal.isSelfApproval">
      This will request a changed hour value. It will not supervisor-approve the record.
    </div>

    <div class="error-message local" *ngIf="modal.errorMessage">
      {{ modal.errorMessage }}
    </div>

    <div class="modal-buttons">
      <button type="button" class="btn btn-secondary" (click)="closeBookedHourChangeModal()">
        Cancel
      </button>

      <button type="button" class="btn btn-primary" (click)="submitBookedHourChange()" [disabled]="isActionRunning$ | async">
        Continue
      </button>
    </div>
  </div>
</div>

<div *ngIf="confirmationAction" class="modal-backdrop">
  <div class="modal confirm-modal" (click)="$event.stopPropagation()">
    <h2>{{ getConfirmationTitle() }}</h2>

    <p class="modal-text">
      {{ getConfirmationMessage() }}
    </p>

    <div class="modal-buttons confirm-buttons">
      <button type="button" class="btn btn-secondary" (click)="cancelConfirmation()">
        Cancel
      </button>

      <button
        type="button"
        class="btn"
        [ngClass]="isConfirmationDanger() ? 'btn-danger' : 'btn-primary'"
        (click)="confirmAction()"
        [disabled]="isActionRunning$ | async">
        {{ getConfirmationButtonText() }}
      </button>
    </div>
  </div>
</div>